<script setup lang="ts">
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import IconCalendar from '~/assets/icons/calendar.svg?component'
import IconPeople from '~/assets/icons/event/people.svg?component'
import IconLocation from '~/assets/icons/event/event-info-location.svg?component'
import type { AccountDateRequest } from '~/composables/types/account-date-request'
import {
  accountDateRequestHasResponse,
  getDateRequestOfferPath,
} from '~/utils/accountDateRequestHelpers'
import { formatPriceAmount } from '~/utils/currencyHelpers'

dayjs.extend(utc)
dayjs.extend(timezone)

const { dateRequest } = defineProps<{
  dateRequest: AccountDateRequest
}>()

const { t, locale } = useI18n()
const localePath = useLocalePath()

const eventDescription = computed(() => dateRequest.eventDescription?.data)

const hasResponse = computed(() => accountDateRequestHasResponse(dateRequest))

const statusVariant = computed(() => {
  if (hasResponse.value) {
    return 'responseAvailable'
  }

  if (dateRequest.status === 'REJECTED') {
    return 'rejected'
  }

  return 'requestSent'
})

const statusLabel = computed(() => {
  if (statusVariant.value === 'responseAvailable') {
    return t('account.dateRequests.status.responseAvailable')
  }

  if (statusVariant.value === 'rejected') {
    return t('account.dateRequests.status.rejected')
  }

  return t('account.dateRequests.status.requestSent')
})

const title = computed(
  () => eventGetters.getTitle(eventDescription.value) || dateRequest.id
)

const imageUrl = computed(() =>
  eventGetters.getFirstThumb(eventDescription.value)
)

const location = computed(() =>
  eventGetters.getAddressLocalityName(eventDescription.value)
)

const peopleText = computed(() =>
  t('account.dateRequests.people', {
    count: dateRequest.requested_tickets,
  })
)

const formattedDates = computed(() => {
  const dates = dateRequest.offerDates?.data ?? []

  if (!dates.length) {
    return null
  }

  const firstDate = dayjs(dates[0].start, { utc: true }).format('DD.MM.YYYY')
  const additionalDates = dates.length - 1

  if (additionalDates <= 0) {
    return firstDate
  }

  return t('account.dateRequests.dateWithMore', {
    date: firstDate,
    count: additionalDates,
  })
})

const formattedPrice = computed(() => {
  const defaultPriceAmount = eventGetters.getDefaultPriceAmount(
    eventDescription.value
  )

  if (defaultPriceAmount == null) {
    return null
  }

  const totalAmount = Number(defaultPriceAmount) * dateRequest.requested_tickets

  return formatPriceAmount(totalAmount, locale.value)
})

const offerLink = computed(() => {
  const offerPath = getDateRequestOfferPath(dateRequest)

  if (!offerPath) {
    return null
  }

  return localePath(offerPath)
})

const buttonLabel = computed(() =>
  hasResponse.value
    ? t('account.dateRequests.viewProposal')
    : t('account.dateRequests.previewEvent')
)
</script>

<template>
  <article class="date-request-card">
    <div class="date-request-card__image">
      <NuxtLazyImg
        v-if="imageUrl"
        :src="imageUrl"
        :alt="title"
        class="date-request-card__image-el"
      />
      <div v-else class="date-request-card__image-placeholder" />

      <span
        class="date-request-card__badge"
        :class="`date-request-card__badge--${statusVariant}`"
      >
        {{ statusLabel }}
      </span>
    </div>

    <div class="date-request-card__body">
      <h3 class="date-request-card__title">{{ title }}</h3>

      <div
        class="date-request-card__details"
        :class="[formattedDates ? 'flex-wrap' : '']"
      >
        <div v-if="formattedDates" class="date-request-card__detail">
          <IconCalendar class="date-request-card__detail-icon" />
          <span>{{ formattedDates }}</span>
        </div>

        <div class="date-request-card__detail">
          <IconPeople class="date-request-card__detail-icon" />
          <span>{{ peopleText }}</span>
        </div>

        <div v-if="location" class="date-request-card__detail">
          <IconLocation class="date-request-card__detail-icon" />
          <span>{{ location }}</span>
        </div>
      </div>

      <div v-if="formattedPrice" class="date-request-card__price">
        {{ formattedPrice }}
      </div>

      <NuxtLink
        v-if="offerLink"
        :to="offerLink"
        class="date-request-card__button"
        :class="{
          'date-request-card__button--primary': hasResponse,
          'date-request-card__button--secondary': !hasResponse,
        }"
      >
        {{ buttonLabel }}
      </NuxtLink>
    </div>
  </article>
</template>

<style lang="postcss" scoped>
.date-request-card {
  @apply flex gap-4 rounded-xl  bg-white;
}

.date-request-card__image {
  @apply relative shrink-0 w-[142px] h-[112px];
}

.date-request-card__image-el,
.date-request-card__image-placeholder {
  @apply w-full h-full rounded-lg object-cover bg-gray-200;
}

.date-request-card__badge {
  @apply absolute top-1 left-1 max-w-[calc(100%-0.5rem)] px-2 py-1 rounded-md text-[10px] leading-tight font-medium;
}

.date-request-card__badge--responseAvailable {
  background: linear-gradient(0deg, #fcffee 0%, #fcffee 100%), #fff;
  box-shadow: 0 4px 16px 0 rgba(31, 42, 55, 0.06);
  color: #333f03;
}

.date-request-card__badge--requestSent {
  background: linear-gradient(0deg, #fffcf3 0%, #fffcf3 100%), #fff;
  box-shadow: 0 4px 16px 0 rgba(31, 42, 55, 0.06);
  color: #9f5900;
}

.date-request-card__badge--rejected {
  @apply bg-stone-100 text-stone-600;
}

.date-request-card__body {
  @apply flex min-w-0 flex-1 flex-col;
}

.date-request-card__title {
  @apply mb-2 line-clamp-2 text-sm font-medium text-stone-900;
}

.date-request-card__details {
  @apply flex  gap-2;
}

.date-request-card__detail {
  @apply flex items-center gap-1 text-xs text-stone-600;
}

.date-request-card__detail-icon {
  @apply h-4 w-4 shrink-0 text-stone-500;
}

.date-request-card__price {
  @apply mt-2 text-sm font-semibold text-stone-900;
}

.date-request-card__button {
  @apply mt-3 inline-flex w-fit items-center justify-center rounded-lg px-4 py-1 text-sm font-semibold no-underline transition-colors;
}

.date-request-card__button--primary {
  @apply bg-teal-900 text-white hover:bg-teal-800;
}

.date-request-card__button--secondary {
  @apply border border-teal-900 bg-white text-teal-900 hover:bg-teal-50;
}
</style>
